import React, { useState, useEffect } from 'react';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import 'firebase/compat/storage';
import { Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input } from 'reactstrap';
import CategorySelect from './CategorySelect';
import FileUpload, {FileUpdateResult} from './FileUpload';
import { Attachment } from '../../models/Attachment.interface';
import { Job } from '../../models/Job.interface';

interface JobEditProps {
  isOpen: boolean;
  toggle: () => void;
  job: Job;
  onSave: (updatedJob: Job) => void;
}

const JobEdit: React.FC<JobEditProps> = ({ isOpen, toggle, job, onSave }) => {
  const [jobData, setJobData] = useState({
    jobTitle: job.jobTitle || '',
    jobSummary: job.jobSummary || '',
    jobDetail: job.jobDetail || '',
    jobCategories: job.jobCategories || [],
    propertyAddress: job.propertyAddress
  });

  const [fileUpdates, setFileUpdates] = useState<FileUpdateResult>({
    newFiles: [],
    deletedUrls: [],
    attachmentOrder: job.jobAttachments || [],
    captions: Object.fromEntries(
      (job.jobAttachments || [])
        .filter(att => att.id != null)
        .map(att => [att.id as string, att.caption || ''])
    )
  });

  const [selectedCategories, setSelectedCategories] = useState<string[]>(job.jobCategories || []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setJobData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileUpdates = (updates: FileUpdateResult) => {
    // Update fileUpdates state with all the changes
    setFileUpdates(prev => ({
      newFiles: updates.newFiles || prev.newFiles,
      // Fixed the Set spread operator TypeScript error by using Array.from
      deletedUrls: Array.from(new Set([
        ...prev.deletedUrls, 
        ...(updates.deletedUrls || [])
      ])),
      attachmentOrder: updates.attachmentOrder || prev.attachmentOrder,
      captions: {
        ...prev.captions,
        ...updates.captions
      }
    }));
  };

  const handleSave = async () => {
    try {
      const storage = firebase.storage();
      const firestore = firebase.firestore();
  
      // Upload new files and create attachments for them
      const uploadPromises = fileUpdates.newFiles.map(async file => {
        const fileRef = storage.ref(`jobs/${job.id}/${Date.now()}_${file.name}`);
        await fileRef.put(file);
        const url = await fileRef.getDownloadURL();
        return {
          id: `attachment-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          url,
          caption: '',
          order: 0
        };
      });
  
      const newAttachments = await Promise.all(uploadPromises);
  
      // Filter out deleted files from current attachments
      const currentAttachments = fileUpdates.attachmentOrder.filter(
        attachment => !fileUpdates.deletedUrls.includes(attachment.url)
      );
  
      // Combine current and new attachments, maintaining order
      const updatedAttachments: Attachment[] = [
        ...currentAttachments,
        ...newAttachments
      ].map((attachment, index) => ({
        ...attachment,
        order: index,
        // Fixed the TypeScript error with optional chaining and type checking
        caption: attachment.id && fileUpdates.captions ? 
          fileUpdates.captions[attachment.id] || attachment.caption || '' 
          : attachment.caption || ''
      }));
  
      const updatedJob: Job = {
        ...job,
        jobTitle: jobData.jobTitle,
        jobSummary: jobData.jobSummary,
        jobDetail: jobData.jobDetail,
        jobCategories: selectedCategories,
        jobAttachments: updatedAttachments
      };
  
      // Delete removed files from storage
      await Promise.all(
        fileUpdates.deletedUrls.map(async (url) => {
          try {
            const fileRef = storage.refFromURL(url);
            await fileRef.delete();
          } catch (error) {
            console.error('Error deleting file from storage:', error);
          }
        })
      );
  
      // Update Firestore
      await firestore.collection('jobs').doc(job.id).update(updatedJob);
  
      // Call onSave with the updated job
      onSave(updatedJob);
      toggle();
    } catch (error) {
      console.error('Error saving job:', error);
    }
  };
  

  useEffect(() => {
    if (job.jobAttachments) {
      setFileUpdates({
        newFiles: [],
        deletedUrls: [],
        attachmentOrder: job.jobAttachments,
        captions: Object.fromEntries(
          job.jobAttachments.map(att => [att.id, att.caption || ''])
        )
      });
    }
  }, [job]);

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl">
      <ModalHeader toggle={toggle}>Edit Job</ModalHeader>
      <ModalBody>
        <Form>
          <FormGroup>
            <Label for="jobTitle">Job Title</Label>
            <Input
              type="text"
              name="jobTitle"
              id="jobTitle"
              value={jobData.jobTitle}
              onChange={handleInputChange}
            />
          </FormGroup>

          <FormGroup>
            <Label for="jobSummary">Job Summary</Label>
            <Input
              type="textarea"
              name="jobSummary"
              id="jobSummary"
              value={jobData.jobSummary}
              onChange={handleInputChange}
              rows={4}
            />
          </FormGroup>

          <FormGroup>
            <Label for="jobDetail">Job Details</Label>
            <Input
              type="textarea"
              name="jobDetail"
              id="jobDetail"
              value={jobData.jobDetail}
              onChange={handleInputChange}
              rows={6}
            />
          </FormGroup>

          <FormGroup>
            <CategorySelect
              selectedCategories={selectedCategories}
              onChange={setSelectedCategories}
              maxSelections={5}
            />
          </FormGroup>

          <FormGroup>
            <Label>Files & Attachments</Label>
            <FileUpload
              onFilesChange={(files) => {
                handleFileUpdates({
                  ...fileUpdates,
                  newFiles: files
                });
              }}
              existingFiles={job.jobAttachments}
              onExistingFileDelete={(url) => {
                handleFileUpdates({
                  ...fileUpdates,
                  deletedUrls: [...fileUpdates.deletedUrls, url],
                  attachmentOrder: fileUpdates.attachmentOrder.filter(att => att.url !== url)
                });
              }}
              onAttachmentUpdate={handleFileUpdates}
              jobId={job.id}
            />
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <button className="btn btn-secondary" onClick={toggle}>Cancel</button>
        <button className="btn btn-primary" onClick={handleSave}>Save Changes</button>
      </ModalFooter>
    </Modal>
  );
};

export default JobEdit;